/** Default proposal send copy + optional custom templates from app_settings. */

export const DEFAULT_PROPOSAL_SMS_TEMPLATE = `Hey {first_name}, please use the link below to view the proposal details

{url}`;

export const DEFAULT_PROPOSAL_EMAIL_BODY_TEMPLATE = `Hi {first_name},

Thank you for asking us to quote on your project.

If you have any questions or concerns regarding this quote, please don't hesitate to get in touch with us.`;

export function fillProposalMessageTemplate(
  template: string,
  vars: {
    firstName?: string;
    url?: string;
    proposalTitle?: string;
    businessName?: string;
  }
): string {
  return template
    .replace(/\{first_name\}/gi, vars.firstName || 'there')
    .replace(/\{url\}/gi, vars.url || '')
    .replace(/\{proposal_title\}/gi, vars.proposalTitle || '')
    .replace(/\{business_name\}/gi, vars.businessName || '');
}

/**
 * Resolve the SMS/email body to show in the send modal.
 * Custom templates are off by default; when on, use the stored template
 * (still editable in the modal).
 */
export async function loadProposalSendMessage(params: {
  businessId: string;
  channel: 'sms' | 'email';
  firstName: string;
  url?: string;
  proposalTitle?: string;
  businessName?: string;
}): Promise<string> {
  const { businessId, channel, firstName, url, proposalTitle, businessName } = params;
  const enabledKey = channel === 'sms' ? 'custom_sms_message_enabled' : 'custom_email_message_enabled';
  const messageKey = channel === 'sms' ? 'custom_sms_message' : 'custom_email_message';
  const builtIn =
    channel === 'sms' ? DEFAULT_PROPOSAL_SMS_TEMPLATE : DEFAULT_PROPOSAL_EMAIL_BODY_TEMPLATE;

  try {
    const { supabase } = await import('./supabase');
    if (!supabase) {
      return fillProposalMessageTemplate(builtIn, { firstName, url, proposalTitle, businessName });
    }

    const [enabledRes, messageRes] = await Promise.all([
      supabase.rpc('get_app_setting', {
        p_business_id: businessId,
        p_app_slug: 'proposals',
        p_setting_key: enabledKey,
        p_default_value: false,
      }),
      supabase.rpc('get_app_setting', {
        p_business_id: businessId,
        p_app_slug: 'proposals',
        p_setting_key: messageKey,
        p_default_value: '',
      }),
    ]);

    const enabled = !!enabledRes.data && !enabledRes.error;
    const custom =
      !messageRes.error && typeof messageRes.data === 'string' ? messageRes.data.trim() : '';

    const template = enabled && custom ? custom : builtIn;
    let filled = fillProposalMessageTemplate(template, {
      firstName,
      url,
      proposalTitle,
      businessName,
    });

    // SMS always needs the link — append if custom template omitted {url}
    if (channel === 'sms' && url && !filled.includes(url)) {
      filled = `${filled.trim()}\n\n${url}`;
    }

    return filled;
  } catch (error) {
    console.error('Error loading proposal send message setting:', error);
    return fillProposalMessageTemplate(builtIn, { firstName, url, proposalTitle, businessName });
  }
}
